/**
 * Online-reconnect flush effect.
 *
 * When connectivity flips from offline to online during an active recording:
 *
 *   1. If the meeting is still provisional (no server row yet because the
 *      session started offline), call startBrowserMeeting to create it
 *      against the deferred meetingId so subsequent uploads land cleanly.
 *   2. Flush any pending chunks immediately so the gap between offline
 *      and online doesn't bloat the next periodic upload.
 *
 * The provisional flag flips back to false on success — repeated transient
 * disconnects after that don't re-trigger meeting creation.
 */
import { useEffect } from "react"
import type { CompanyApi } from "../../../hooks/useCompanyApi"
import { startBrowserMeeting } from "../../../lib/api"
import type { IRemoteUploadHandle, TRecordingState } from "../interfaces"

interface IProps {
  meetingId: string
  subject?: string
  templateId?: string
  isOnline: boolean
  provisionalPending: boolean
  company: CompanyApi | null
  stateRef: React.MutableRefObject<TRecordingState>
  chunksRef: React.MutableRefObject<Blob[]>
  doChunkUpload: (isFinal: boolean) => Promise<boolean>
  setProvisionalPending: (pending: boolean) => void
  /** Desktop hybrid far-end channel — flushed (remote then room) on reconnect. */
  remoteUpload?: IRemoteUploadHandle
}

export function useOnlineFlush(props: IProps): void {
  const {
    meetingId,
    subject,
    templateId,
    isOnline,
    provisionalPending,
    company,
    stateRef,
    chunksRef,
    doChunkUpload,
    setProvisionalPending,
    remoteUpload,
  } = props

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — only run when isOnline flips true
  useEffect(() => {
    if (!isOnline) return

    const flush = async (): Promise<void> => {
      if (provisionalPending && company && subject) {
        try {
          await startBrowserMeeting(company, subject, templateId, meetingId)
          setProvisionalPending(false)
          sessionStorage.removeItem(`provisional:${meetingId}`)
        } catch {
          return
        }
      }

      if (stateRef.current === "recording" || stateRef.current === "paused") {
        // Flush the far-end backlog first, then the mic, so a complete remote
        // file is on its way before the room channel races ahead. Both are
        // non-final here — neither triggers processing until finish.
        if (remoteUpload && remoteUpload.chunksRef.current.length > 0) {
          await remoteUpload.doChunkUpload(false).catch(() => {})
        }
        if (chunksRef.current.length > 0) {
          doChunkUpload(false).catch(() => {})
        }
      }
    }

    flush().catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when connectivity changes
  }, [isOnline])
}
